export const searchEventsConfig = {
    // Use the specific search results template container
    parentSelector: 'div.template-search',
    events: {
        // NOTE: Modal search events (focus, input, submit, suggestions) moved to headerEventsConfig.js
        
        // ========================================
        // == Search Bar Events (Search Page) ==
        // ========================================
        
        // CSV Line 13: Search | focus in text field on search page - Optional
        searchPageFocus: {
            selector: 'input#Search-In-Template', 
            event: {
                event: 'search',
                component: 'search_bar',
                element: 'text_field',
                action: 'focus',
                trigger: 'focus'
            }
        },
        
        // CSV Line 14: Search | input in text field on search page
        searchPageInput: {
            selector: 'input#Search-In-Template',
            event: {
                event: 'search',
                component: 'search_bar', 
                element: 'text_field',
                action: 'input',
                trigger: 'input', // Debounced by datalayer.js
                getDynamicData: (element) => ({
                    search_term: element.value
                })
            }
        },
        
        // CSV Line 15: Search | submit search on search page (Enter or Button)
        searchPageSubmit: {
            selector: 'form.template-search__search__form', // Target the form itself for submit event
            event: {
                event: 'search',
                component: 'search_bar', 
                element: 'text_field', // Based on CSV
                action: 'submit',
                trigger: 'submit', 
                getDynamicData: (element) => { // element is the form
                    const input = element.querySelector('input#Search-In-Template');
                    return {
                        search_term: input ? input.value : '',
                        // Construct URL from form action and input value
                        link_url: element ? `${element.action}?q=${encodeURIComponent(input?.value || '')}&options%5Bprefix%5D=last` : ''
                    };
                }
            }
        },

        // ========================================
        // == Filter Events (Search Page) ==
        // ========================================
        
        // CSV Line 16: Search | click on button hide/show filter (Desktop only)
        filterToggleButton: {
            selector: 'button.facets-active-toolbar__toggle-filter', // Specific button class
            event: {
                event: 'search',
                component: 'filter',
                element: 'button',
                action: 'click',
                trigger: 'click',
                getDynamicData: (element) => {
                    // Get text from the appropriate span based on visibility
                    const showText = element.querySelector('.show-filter-text')?.textContent.trim();
                    const hideText = element.querySelector('.hide-filter-text')?.textContent.trim();
                    // Logic might need refinement based on exactly how visibility is toggled
                    // Assuming parent class dictates state
                    const facetsVertical = element.closest('.facets-vertical'); 
                    const isShowingFilters = !facetsVertical?.classList.contains('facets-vertical--hide-filter');
                    return { click_text: isShowingFilters ? hideText : showText }; 
                }
            }
        },

        // CSV Line 21: Search | filter accordion open/close
        filterAccordionToggle: {
             // Target both desktop and mobile accordion summaries
            selector: 'details.facets__details summary.facets__summary', 
            event: {
                event: 'search',
                component: 'filter',
                element: 'accordion',
                // Action determined by the state *before* the click
                action: 'click', // Simplified action, dynamic data specifies open/close
                trigger: 'click',
                getDynamicData: (element) => {
                    const detailsElement = element.closest('details');
                    const isOpen = detailsElement?.hasAttribute('open');
                    const labelElement = element.querySelector('.facets__summary__label');
                    return {
                        field_name: labelElement ? labelElement.textContent.trim() : 'Unknown Filter',
                        action: isOpen ? 'close' : 'open' // State *after* click
                    };
                }
            }
        },
        
        // CSV Line 23: Search | filter checkbox on/off
        filterCheckboxToggle: {
            selector: '#FacetFiltersForm input[type="checkbox"].facets__checkbox-value, #FacetFiltersFormMobile input[type="checkbox"].facets__checkbox-value',
            event: {
                event: 'search',
                component: 'filter',
                element: 'checkbox',
                action: 'click', // Simplified action
                trigger: 'change',
                getDynamicData: (element) => {
                    const labelElement = element.closest('label.facet-checkbox')?.querySelector('.facet-checkbox__content__label');
                    const accordionSummary = element.closest('details.facets__details')?.querySelector('summary .facets__summary__label');
                    return {
                        field_name: accordionSummary ? accordionSummary.textContent.trim() : 'Unknown Filter',
                        checkbox_value: labelElement ? labelElement.textContent.trim() : 'Unknown Value',
                        action: element.checked ? 'on' : 'off' // Reflect state *after* change
                    };
                }
            }
        },
        
        // CSV Line 25: Search | filter select with slider (Price Range)
        filterPriceRangeSelect: {
            selector: 'price-range input[type="range"]',
            event: {
                event: 'search',
                component: 'filter',
                element: 'slider',
                field_name: 'Prijs', // Static for price slider
                action: 'select',
                trigger: 'change', // Trigger when slider value changes (mouseup)
                getDynamicData: (element) => {
                    const priceRangeElement = element.closest('price-range');
                    const fromValue = priceRangeElement?.querySelector('.facets__price__range-value--from')?.textContent.trim();
                    const toValue = priceRangeElement?.querySelector('.facets__price__range-value--to')?.textContent.trim();
                    return {
                        // Format: "Prijs: €0,00 EUR - €2.895,00 EUR"
                        select_value: `Prijs: ${fromValue || '€?.??'} - ${toValue || '€?.??'}` 
                    };
                }
            }
        },

        // CSV Line 26/27: Search | filter click on view more/less options
        filterShowMoreLess: {
            selector: 'details.facets__details .facets__display button', // Target the button itself
            event: {
                event: 'search',
                component: 'filter',
                element: 'button',
                action: 'click',
                trigger: 'click',
                getDynamicData: (element) => { // element is the button
                    const detailsElement = element.closest('details.facets__details');
                    const filterName = detailsElement?.querySelector('summary .facets__summary__label')?.textContent.trim();
                    
                    const showMoreSpan = element.querySelector('.label-show-more');
                    const showLessSpan = element.querySelector('.label-show-less');
                    
                    let clickText = ''; // Default value

                    // FLIPPED LOGIC: Capture the text that will be shown AFTER the click
                    if (showLessSpan && !showLessSpan.classList.contains('hidden')) {
                        // If "Minder weergeven" is currently visible, the action is effectively "show more"
                        clickText = 'Meer weergeven'; 
                    } else if (showMoreSpan && !showMoreSpan.classList.contains('hidden')) {
                        // If "Meer weergeven" is currently visible, the action is effectively "show less"
                        clickText = 'Minder weergeven';
                    }
                    
                    return {
                        field_name: filterName || 'Unknown Filter',
                        click_text: clickText
                    };
                }
            }
        },

        // CSV Line 28/29/30: Search | filter modal open/close/cta (Mobile)
        filterModalMobileToggle: {
            selector: 'details.mobile-facets__disclosure summary.mobile-facets__open-wrapper', // Button to open/close mobile filter drawer
            event: {
                event: 'search',
                component: 'filter',
                element: 'modal',
                action: 'click', // Simplified - dynamic data for open/close
                trigger: 'click',
                getDynamicData: (element) => {
                    const detailsElement = element.closest('details');
                    const isOpen = detailsElement?.hasAttribute('open');
                    const buttonText = element.querySelector('.mobile-facets__open-label')?.textContent.trim() || 'Filteren en sorteren';
                    return {
                        action: isOpen ? 'close' : 'open', // State *after* click
                        click_text: buttonText // Text of the button clicked
                    };
                }
            }
        },
        filterModalMobileClear: {
            selector: 'facet-remove.mobile-facets__clear-wrapper a.mobile-facets__clear', // Mobile clear filters button
            event: {
                event: 'search',
                component: 'filter',
                element: 'cta_button',
                action: 'click',
                trigger: 'click',
                getDynamicData: (element) => {
                    return {
                        click_text: element.textContent.trim() || 'Alles verwijderen'
                    };
                }
            }
        },
        filterModalMobileApply: {
            selector: '.mobile-facets__footer button.button--primary', // Mobile apply filters button
            event: {
                event: 'search',
                component: 'filter',
                element: 'cta_button',
                action: 'click',
                trigger: 'click',
                getDynamicData: (element) => {
                    return {
                        click_text: element.textContent.trim() || 'Toepassen'
                    };
                }
            }
        },

        // CSV Line 30: Search | Remove filter/all filters (Desktop)
        filterRemoveSingle: {
            selector: 'facet-remove a.active-facets__button', // Individual active filter remove buttons
            event: {
                event: 'search',
                component: 'filter',
                element: 'button',
                action: 'click',
                trigger: 'click',
                getDynamicData: (element) => {
                    // Extract filter name and value from the button text or attributes
                    const buttonText = element.textContent.trim();
                    return {
                        click_text: buttonText // E.g., "Merk: Bsi", "Prijs: ..."
                    };
                }
            }
        },
        // Note: "Clear all" might be part of filterRemoveSingle if it shares the selector or needs its own

        // ========================================
        // == Sorting Events (Search Page) ==
        // ========================================
        
        // CSV Line 17/18/19: Search | dropdown relevance open/close/select
        sortingDropdownToggleDesktop: {
            selector: 'details.facet-filters__field__details[data-sort="SortByDesktop"] summary.disclosure__button',
            event: {
                event: 'search',
                component: 'sorting',
                element: 'dropdown',
                action: 'click', // Simplified - dynamic data for open/close
                trigger: 'click',
                getDynamicData: (element) => {
                    const detailsElement = element.closest('details');
                    const isOpen = detailsElement?.hasAttribute('open');
                    return {
                        field_name: 'Relevantie', // Assuming this is the default/main sort label
                        action: isOpen ? 'close' : 'open' // State *after* click
                    };
                }
            }
        },
        sortingDropdownSelectDesktop: {
            selector: 'ul#SortByDesktopList button.facet-checkbox--sort-button',
            event: {
                event: 'search',
                component: 'sorting',
                element: 'dropdown',
                action: 'select',
                trigger: 'click',
                getDynamicData: (element) => {
                    return {
                        field_name: 'Relevantie', // Main sort label
                        dropdown_select_value: element.textContent.trim()
                    };
                }
            }
        },
        sortingSelectMobile: {
            selector: 'select#SortBy-mobile', // Mobile sorting select element
            event: {
                event: 'search',
                component: 'sorting',
                element: 'dropdown',
                action: 'select',
                trigger: 'change',
                getDynamicData: (element) => {
                    const selectedOption = element.options[element.selectedIndex];
                    return {
                        field_name: 'Relevantie', // Main sort label
                        dropdown_select_value: selectedOption ? selectedOption.text : ''
                    };
                }
            }
        },
        
        // ========================================
        // == Pagination Events (Search Page) ==
        // ========================================
        
        // CSV Line 33/34/35: Search | pagination click on arrow > / < / number
        paginationClick: {
            selector: 'nav.pagination a.pagination__item', // Target all clickable pagination items
            event: {
                event: 'search',
                component: 'pagination',
                // Element type and action_value determined dynamically
                action: 'click',
                trigger: 'click',
                getDynamicData: (element) => {
                    let elementType = 'unknown';
                    let actionValue = element.textContent.trim();
                    
                    if (element.classList.contains('pagination__item--prev')) {
                        elementType = 'chevron';
                        actionValue = 'previous';
                    } else if (element.classList.contains('pagination__item--next')) {
                         elementType = 'chevron';
                         actionValue = 'next';
                    } else if (element.classList.contains('pagination__item--number')) {
                        elementType = 'number';
                        // actionValue is already the page number text
                    }
                    
                    return {
                        element: elementType,
                        page_number: elementType === 'number' ? actionValue : undefined,
                        action_value: elementType === 'chevron' ? actionValue : undefined
                    };
                }
            }
        }

        // NOTE: Product card click/ATC events removed - will be in searchProductCardEventsConfig.js
    }
}; 